import React, { Fragment, useEffect } from "react";
import { withRouter, Route, Redirect } from "react-router-dom";
import { connect } from "react-redux";

import { passwordResetAuthority } from "services/authProviderReset";
import { defaultScopes } from "services/authProvider";

import HeaderFactory from "../components/Header/HeaderFactory";
import FooterFactory from "components/Footer/FooterFactory";
import Loader from "components/Loader";
import FreeTrialPopup from "components/FreeTrialModal";
import SubscriptionPopup from "components/SubscriptionModal";
import ErrorModal from "../components/ErrorModal";
import MultifactorAuth from "pages/Auth/components/MFA";
import SenbirdChatCallWrapper from "patient-scheduling/wrapper";

import PublicRoutes from "../routes/PublicRoutes";
import OnlineFaqRoutes from "../routes/OnlineFaqRoutes";

import constants from "./../constants";

import {
  getProfile,
  getSubscriptionStatus,
  getCompanyInformation,
} from "actions/index";

import { AppContextProvider, useAppContext } from "context/AppContext";
import IsAccessibleChatAssitant from "aiAssitant/ChatAssistant";
import Helper from "utils/helper";
import { readMfaValidatedRaw } from "utils/mfaStorage";
import { isLogoutInProgress, clearLogoutSessionFlags } from "utils/appLogout";
import { isProtectedDeepLink } from "utils/protectedDeepLink";

const LOGIN_REDIRECT_STUCK_MS = 45000;

const AppContent = ({
  profile,
  isLoading,
  isError,
  profileError,
  profileErrorMessage,
  networkError,
  companyInformation,
  children,
  location,
}) => {
  const {
    instance,
    signinHandler,
    handleSignup,
    logout,
    logoutFromMfa,
    isMfaValidate,
    isMfaChecking,
    mfaHomePreflightDone,
    msalTokenReady,
    statusModal,
    closeModal,
    shouldShowTrialModal,
    errorType,
    account,
    isAuthenticated,
    msalStartupPending,
    msalAccountRecovering,
    isLoginRedirectPending,
    cancelPendingLoginRedirect,
  } = useAppContext();

  useEffect(() => {
    if (errorType) {
      instance.loginRedirect({
        authority: passwordResetAuthority,
        scopes: defaultScopes,
      });
    }
  }, [errorType, instance]);

  useEffect(() => {
    if (isAuthenticated || Helper.isLoggedIn()) {
      clearLogoutSessionFlags();
      sessionStorage.removeItem(
        constants.sessionStorageKeys.pendingLoginRedirect,
      );
      return;
    }

    if (
      location?.pathname === "/" &&
      (sessionStorage.getItem(
        constants.sessionStorageKeys.postLogoutPublicNav,
      ) ||
        isLogoutInProgress())
    ) {
      clearLogoutSessionFlags();
    }
  }, [isAuthenticated, location?.pathname]);

  const email = companyInformation?.email || "";
  const phone = companyInformation?.phone || "";
  const OnlineFaqPagesCheck = constants.OnlineFaqPages.includes(
    window.location.pathname,
  );
  const pendingLoginRedirect = sessionStorage.getItem(
    constants.sessionStorageKeys.pendingLoginRedirect,
  );
  const loginRedirectActive = Boolean(pendingLoginRedirect || isLoginRedirectPending);
  const loggedOutProtectedDeepLink =
    !isAuthenticated &&
    !Helper.isLoggedIn() &&
    !readMfaValidatedRaw() &&
    isProtectedDeepLink(location?.pathname);

  /** Release the login loader if B2C redirect never started (avoids indefinite block). */
  useEffect(() => {
    if (
      (!loginRedirectActive && !loggedOutProtectedDeepLink) ||
      isAuthenticated ||
      Helper.isLoggedIn() ||
      isLogoutInProgress()
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      if (Helper.isLoggedIn() || isAuthenticated) {
        return;
      }
      if (isProtectedDeepLink(window.location.pathname)) {
        signinHandler();
        return;
      }
      cancelPendingLoginRedirect();
    }, LOGIN_REDIRECT_STUCK_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    loginRedirectActive,
    loggedOutProtectedDeepLink,
    isAuthenticated,
    cancelPendingLoginRedirect,
    signinHandler,
  ]);

  /** MSAL logoutRedirect in flight — avoid client-side Redirect to `/` before B2C returns. */
  if (isLogoutInProgress()) {
    return (
      <div className="main-wrapper">
        <Loader />
      </div>
    );
  }
  /** MSAL hooks can lag behind localStorage; mounting PublicRoutes on a private URL hits `*` → /404. */
  const sessionRestoring =
    Helper.isLoggedIn() || readMfaValidatedRaw();
  /**
   * Private email links (policy details, events, and the rest) are not public
   * routes. Rendering them here sends `*` to /404 and drops the return URL.
   */
  if (
    (loginRedirectActive || loggedOutProtectedDeepLink) &&
    !Helper.isLoggedIn() &&
    !isAuthenticated
  ) {
    return (
      <div className="main-wrapper">
        <Loader />
      </div>
    );
  }
  if (errorType) {
    return (
      <div className="main-wrapper">
        <Loader />
      </div>
    );
  }

  if (msalStartupPending && !sessionRestoring && !OnlineFaqPagesCheck) {
    return (
      <div className="main-wrapper">
        <Loader />
      </div>
    );
  }

  /** Do not replace the MFA screen with a blank loader while URL is `/authenticate`. */
  if (
    !isAuthenticated &&
    sessionRestoring &&
    !OnlineFaqPagesCheck &&
    location?.pathname !== "/authenticate"
  ) {
    return (
      <div className="main-wrapper">
        <Loader />
      </div>
    );
  }

  /**
   * Token + MFA in storage but MSAL account not yet resolved (Edge cold start).
   * Show loader only while recovery is in flight — synthetic account renders once ready.
   */
  if (
    !account &&
    sessionRestoring &&
    (msalAccountRecovering || msalStartupPending) &&
    !OnlineFaqPagesCheck &&
    location?.pathname !== "/authenticate"
  ) {
    return (
      <div className="main-wrapper">
        <Loader />
      </div>
    );
  }

  const showMfaShell =
    isAuthenticated ||
    (location?.pathname === "/authenticate" && Helper.isLoggedIn());

  return (
    <SenbirdChatCallWrapper>
      <div className="main-wrapper">
        <IsAccessibleChatAssitant />
        {OnlineFaqPagesCheck && <OnlineFaqRoutes />}

        {showMfaShell && (
          <>
            {!isMfaValidate &&
              !profileError &&
              !networkError &&
              window.location.pathname !== "/authenticate" &&
              window.location.pathname !== "/Offices" && (
                <>
                  {!msalTokenReady ||
                  isMfaChecking ||
                  !mfaHomePreflightDone ? (
                    <Loader />
                  ) : (
                    <Redirect to="/authenticate" />
                  )}
                </>
              )}
            <Route
              exact
              path="/authenticate"
              render={() => (
                <MultifactorAuth account={account} logoutFromMfa={logoutFromMfa} />
              )}
            />
          </>
        )}

        {!isAuthenticated &&
          !OnlineFaqPagesCheck &&
          !(location?.pathname === "/authenticate" && Helper.isLoggedIn()) && (
          <div>
            {/*
              After MFA "Back to login" or B2C logout, MSAL can clear the session
              while the URL is still /authenticate. PublicRoutes has no /authenticate
              route and its * catch-all sends users to /404 — show home instead.
            */}
            {location?.pathname === "/authenticate" ? (
              <Redirect to="/" />
            ) : (
              <>
                <HeaderFactory onLogin={signinHandler} onSignup={handleSignup} />
                <PublicRoutes
                  signinHandler={signinHandler}
                  signupHandler={handleSignup}
                />
                <FooterFactory />
              </>
            )}
          </div>
        )}

        {isAuthenticated &&
          account &&
          !OnlineFaqPagesCheck &&
          location.pathname !== "/authenticate" && (
          <div>
            {profile && (
              <HeaderFactory
                providerName={account?.idTokenClaims?.acr}
              />
            )}
            <div className="main-content">
              {isLoading && !isError && !profileError && !networkError ? (
                <Loader />
              ) : (
                <Fragment>
                  {children}
                  <ErrorModal
                    isOpen={profileError}
                    message={profileErrorMessage}
                    email={email}
                    phone={phone}
                    showContactInfo={true}
                    onLogout={logout}
                  />
                  <ErrorModal
                    isOpen={networkError}
                    message={profileErrorMessage}
                    email={email}
                    phone={phone}
                    showContactInfo={!networkError}
                    onLogout={logout}
                  />
                </Fragment>
              )}
              <FreeTrialPopup
                data={profile}
                show={shouldShowTrialModal()}
                closeModal={closeModal}
              />
              <SubscriptionPopup show={statusModal} closeModal={closeModal} />
            </div>
            {profile && <FooterFactory role={profile.role} />}
          </div>
        )}
      </div>
    </SenbirdChatCallWrapper>
  );
};

const App = (props) => {
  return (
    <AppContextProvider
      profile={props.profile}
      subscriptionStatus={props.subscriptionStatus}
      getProfile={props.getProfile}
      getSubscriptionStatus={props.getSubscriptionStatus}
      getCompanyInformation={props.getCompanyInformation}
    >
      <AppContent {...props} />
    </AppContextProvider>
  );
};

const mapStateToProps = ({
  userProfile: {
    profile,
    isLoading,
    profileError,
    profileErrorMessage,
    networkError,
  },
  errors: { isError },
  pageContent: { companyInformation },
  sub: { subscriptionStatus },
}) => ({
  profile,
  isLoading,
  isError,
  profileError,
  profileErrorMessage,
  networkError,
  subscriptionStatus,
  companyInformation,
});

export default connect(mapStateToProps, {
  getProfile,
  getSubscriptionStatus,
  getCompanyInformation,
})(withRouter(App));
